import { type KeyboardEvent, useRef, useState } from 'react'

import { AutoResizeTextarea } from '@/components/ui/AutoResizeTextarea'
import { cn } from '@/utils'
import { EditMark } from './EditMark'
import { FIELD_AT_REST, FIELD_EMPTY, FIELD_OPEN } from './fieldBox'
import { InlineFieldHint } from './InlineFieldHint'

// The lines a bank name or an owner can wrap onto while it is being typed.
const OPEN_FIELD_ROWS = 3

interface InlineTextProps {
  value: string
  shown?: string
  placeholder: string
  title: string
  className: string
  onCommit: (value: string) => void
  dataAttribute?: Record<string, string | number>
  // The box is as wide as the column unless the value is a few digits.
  openWidth?: string
}

// Text until it is clicked, then the same words in a box that wraps as they do. The typing is
// held back until the field is let go, so one correction is one change to undo, not one per key.
export const InlineText = ({
  value,
  shown = value,
  placeholder,
  title,
  className,
  onCommit,
  dataAttribute,
  openWidth = 'w-[calc(100%+0.5rem)]',
}: InlineTextProps) => {
  const [draft, setDraft] = useState<string | null>(null)
  const dropped = useRef(false)

  if (draft === null) {
    return (
      <button
        type="button"
        title={title}
        onClick={() => setDraft(value)}
        className={cn(FIELD_AT_REST, className, !value && FIELD_EMPTY)}
      >
        <span className="min-w-0">{shown || placeholder}</span>
        <EditMark />
      </button>
    )
  }

  const commit = () => {
    if (!dropped.current && draft.trim() !== value) onCommit(draft.trim())
    dropped.current = false
    setDraft(null)
  }

  // Enter saves rather than breaking the line: none of these values has a second line.
  const shortcuts = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
    }
    if (event.key !== 'Escape') return
    dropped.current = true
    event.currentTarget.blur()
  }

  return (
    <span className="flex w-full min-w-0 flex-col items-start">
      <AutoResizeTextarea
        {...dataAttribute}
        // The box only exists because the text was just clicked, so the caret goes straight in.
        ref={(box) => box?.focus()}
        value={draft}
        maxRows={OPEN_FIELD_ROWS}
        placeholder={placeholder}
        title={title}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={shortcuts}
        className={cn(FIELD_OPEN, className, openWidth)}
      />
      <InlineFieldHint />
    </span>
  )
}
